import { motion } from 'framer-motion';
import { Plus } from 'lucide-react';
import { useLanguage, useSeo } from '../i18n/LanguageContext';
import { IMAGES } from '../data/images';
import { Eyebrow, MaskedLines, Reveal, EASE } from '../components/bits';
import PageHero from '../components/PageHero';
import FinalCTA from '../components/FinalCTA';

function SeasonBlock({ season, index }) {
  const { t } = useLanguage();
  const flip = index % 2 === 1;
  return (
    <section
      id={season.id}
      data-testid={`season-${season.id}`}
      className={`py-16 md:py-24 ${flip ? 'noise-bg bg-cream' : 'bg-white'}`}
    >
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-6 sm:px-12 lg:grid-cols-12 lg:gap-16 lg:px-20">
        <Reveal className={`lg:col-span-6 ${flip ? 'lg:order-2' : ''}`}>
          <div className="overflow-hidden">
            <motion.img
              src={IMAGES[season.img]}
              alt={season.alt}
              loading="lazy"
              initial={{ scale: 1.08 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1.2, ease: EASE }}
              className="aspect-[16/10] w-full object-cover"
            />
          </div>
        </Reveal>
        <div className={`lg:col-span-6 ${flip ? 'lg:order-1' : ''}`}>
          <span className="font-mono text-xs font-semibold tracking-[0.3em] text-[#C6A15B]">
            0{index + 1}
          </span>
          <MaskedLines
            as="h2"
            lines={[season.name]}
            inView
            delay={0.05}
            className="mt-4 font-display text-3xl font-extrabold uppercase tracking-tight text-ink sm:text-4xl lg:text-5xl"
          />
          <Reveal delay={0.15}>
            <p className="mt-5 max-w-md text-base leading-relaxed text-neutral-600 sm:text-lg">{season.text}</p>
          </Reveal>
          {season.items.length > 0 && (
            <Reveal delay={0.2}>
              <p className="mt-8 font-mono text-[11px] font-semibold uppercase tracking-[0.3em] text-forest">
                {t.seasonal.possible}
              </p>
              <ul className="mt-4 space-y-2.5">
                {season.items.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-neutral-700">
                    <Plus className="mt-0.5 h-4 w-4 shrink-0 text-[#C6A15B]" strokeWidth={2.5} aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
            </Reveal>
          )}
          {season.note && (
            <Reveal delay={0.2}>
              <p className="mt-8 max-w-md border-l-2 border-[#C6A15B] pl-5 text-sm leading-relaxed text-neutral-600">
                {season.note}
              </p>
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
}

export default function Seasonal() {
  const { t } = useLanguage();
  useSeo('seasonal');
  const s = t.seasonal;

  return (
    <main>
      <PageHero eyebrow={s.eyebrow} lines={[s.line1, s.line2]} sub={s.intro} img={IMAGES.seasonalHero} alt={s.seasons[0].alt} tall />
      {s.seasons.map((season, i) => (
        <SeasonBlock key={season.id} season={season} index={i} />
      ))}
      <FinalCTA />
    </main>
  );
}
